import { Transition } from '@headlessui/react';
import React, {
  createContext,
  FC,
  type JSX,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import Toast, { ToastProps as ToastData } from './Toast';

export type { ToastData };

type TSimpleToastOptions = {
  dismissible?: boolean;
  onDismiss?: () => void;
};

type TToastContext = {
  toasts: ToastData[];
  pushToast: (toast: ToastData) => void;
  pushSuccess: (
    message: string | JSX.Element,
    options?: TSimpleToastOptions
  ) => void;
  pushError: (
    message: string | JSX.Element,
    options?: TSimpleToastOptions
  ) => void;
  pushWarning: (
    message: string | JSX.Element,
    options?: TSimpleToastOptions
  ) => void;
  pushInfo: (
    message: string | JSX.Element,
    options?: TSimpleToastOptions
  ) => void;
};

const ToastContext = createContext<TToastContext>({
  toasts: [],
  pushToast: () => {},
  pushSuccess: () => {},
  pushError: () => {},
  pushWarning: () => {},
  pushInfo: () => {},
});

export const useToastContext = () => {
  return React.useContext(ToastContext);
};

const ToastContextProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const [toasts, setToasts] = useState<TToastContext['toasts']>([]);

  const pushToast = useCallback(
    (toast: ToastData) => {
      setToasts((prevToasts) => {
        return [...(prevToasts || []), toast];
      });
    },
    [setToasts]
  );

  const pushSuccess = useCallback(
    (message: string | JSX.Element, options?: TSimpleToastOptions) => {
      pushToast({ variant: 'white', icon: 'success', message, ...options });
    },
    [pushToast]
  );

  const pushError = useCallback(
    (message: string | JSX.Element, options?: TSimpleToastOptions) => {
      pushToast({ variant: 'white', icon: 'error', message, ...options });
    },
    [pushToast]
  );

  const pushWarning = useCallback(
    (message: string | JSX.Element, options?: TSimpleToastOptions) => {
      pushToast({ variant: 'white', icon: 'warning', message, ...options });
    },
    [pushToast]
  );

  const pushInfo = useCallback(
    (message: string | JSX.Element, options?: TSimpleToastOptions) => {
      pushToast({ variant: 'white', icon: 'info', message, ...options });
    },
    [pushToast]
  );

  // While there are toasts, remove the first toast after 5 seconds
  useEffect(() => {
    if (toasts.length > 0) {
      const timer = setTimeout(() => {
        setToasts((prevToasts) => {
          return prevToasts.slice(1);
        });
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [toasts]);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        pushToast,
        pushSuccess,
        pushError,
        pushWarning,
        pushInfo,
      }}
    >
      {children}

      {!!toasts.length && (
        <>
          {createPortal(
            <div className="z-toast fixed bottom-[50px] right-0 space-y-4 p-8">
              {toasts.map((toast, index) => (
                <Transition
                  key={index}
                  show
                  appear
                  as="div"
                  enter="transition-opacity transition-transform duration-75"
                  enterFrom="opacity-0 translate-x-full"
                  enterTo="opacity-100"
                >
                  <Toast
                    {...toast}
                    onDismiss={() => {
                      toast.onDismiss?.();

                      setToasts((prevToasts) => {
                        return prevToasts.filter((_, i) => i !== index);
                      });
                    }}
                  />
                </Transition>
              ))}
            </div>,
            document.body
          )}
        </>
      )}
    </ToastContext.Provider>
  );
};

export default ToastContextProvider;
